import { useEffect, useState } from 'react'
import mapCleanSat from '@assets/baseMaps/map-cl-sat.png'
import mapRoadDark from '@assets/baseMaps/map-road-dark.png'
import mapRoadLight from '@assets/baseMaps/map-road-light.png'
import mapSat from '@assets/baseMaps/map-sat.png'
import mapTerrain from '@assets/baseMaps/map-terrain.png'
import chartARC from '@assets/chart-arc.png'
import chartENRCHigh from '@assets/chart-enrc_high.png'
import chartENRCLow from '@assets/chart-enrc_low.png'
import chartREA from '@assets/chart-rea.png'
import chartREH from '@assets/chart-reh.png'
import chartREUL from '@assets/chart-reul.png'
import chartWAC from '@assets/chart-wac.png'
import userChartPlaceholder from '@assets/user-chart-placeholder.png'
import { Accordion, AccordionItem } from '@nexds/web'

import { InjectionTokens } from '@/controller/tokens'

import { UserChartChanged } from '@/domain/events/UserChartChanged'
import { UserChartSynced } from '@/domain/events/UserChartSynced'
import { UserChart } from '@/domain/models/UserChart'
import {
  BaseChartType,
  BaseMapType,
  MapLayerState,
  MapLayerStateProps,
  Overlay2ChartType,
  Overlay3ChartType,
  OverlayChartType
} from '@/domain/states/MapLayerState'
import { LoadUserChartsUseCase } from '@/domain/useCases/UserCharts/LoadUserChartsUseCase'

import { ChartSelector } from '../ChartSelector'
import { ChartsContainer, SelectorsGrid } from '../ToolbarTools.styles'

import { MetricsEvents } from '@/constants/metricsEvents'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'
import { useI18n } from '@/presentation/hooks/useI18n'
import { useUserChartMapActivation } from '@/presentation/hooks/useUserChartMapActivation'
import { sendMetrics } from '@/presentation/utils/sendMetrics'
import { ToastUtil } from '@/utils/toastUtil'

type ChartAndMapProps<T = BaseChartType | OverlayChartType | Overlay2ChartType | BaseMapType> = {
  key: string
  chartId: T
  label: string
  image: string
  isActive: boolean
}

function ChartsTool() {
  const [mapLayerState, mapLayerStateMutator] = useGlobalState<MapLayerState, MapLayerStateProps>(
    InjectionTokens.MapLayerState
  )

  const { t } = useI18n()

  const { activateUserChart, activatingChartId } = useUserChartMapActivation()

  const [userCharts, setUserCharts] = useState<UserChart[]>([])

  useEffect(() => {
    const fetchUserCharts = async () => {
      const result = await new LoadUserChartsUseCase().execute()
      setUserCharts(result.isSuccess ? result.getValue() : [])
    }

    fetchUserCharts()

    const changedSub = UserChartChanged.subscribe(fetchUserCharts)
    const syncedSub = UserChartSynced.subscribe(fetchUserCharts)

    return () => {
      changedSub.unsubscribe()
      syncedSub.unsubscribe()
    }
  }, [])

  const overlayCharts: ChartAndMapProps<OverlayChartType>[] = [
    {
      key: 'REA',
      chartId: 'REA',
      label: 'REA',
      image: chartREA,
      isActive: mapLayerState.overlayChart === 'REA'
    },
    {
      key: 'ARC',
      chartId: 'ARC',
      label: 'ARC',
      image: chartARC,
      isActive: mapLayerState.overlayChart === 'ARC'
    }
  ]

  const overlay2Charts: ChartAndMapProps<Overlay2ChartType>[] = [
    {
      key: 'REH',
      chartId: 'REH',
      label: 'REH',
      image: chartREH,
      isActive: mapLayerState.overlay2Chart === 'REH'
    }
  ]
  const overlay3Charts: ChartAndMapProps<Overlay3ChartType>[] = [
    {
      key: 'REUL',
      chartId: 'REUL',
      label: 'REUL',
      image: chartREUL,
      isActive: mapLayerState.overlay3Chart === 'REUL'
    }
  ]
  const baseCharts: ChartAndMapProps<BaseChartType>[] = [
    {
      key: 'WAC',
      chartId: 'WAC',
      label: 'WAC',
      image: chartWAC,
      isActive: mapLayerState.baseChart === 'WAC'
    },
    {
      key: 'ENRCH',
      chartId: 'ENRCH',
      label: 'ENRC High',
      image: chartENRCHigh,
      isActive: mapLayerState.baseChart === 'ENRCH'
    },
    {
      key: 'ENRCL',
      chartId: 'ENRCL',
      label: 'ENRC Low',
      image: chartENRCLow,
      isActive: mapLayerState.baseChart === 'ENRCL'
    }
  ]

  const baseMaps: ChartAndMapProps<BaseMapType>[] = [
    {
      key: 'ROAD',
      chartId: 'ROAD',
      label: t('TOOLBAR_CHARTS-AND-MAPS_BASE-MAPS_ITEM_ROAD-LIGHT_LABEL'),
      image: mapRoadLight,
      isActive: mapLayerState.baseMap === 'ROAD'
    },
    {
      key: 'ROAD-DARK',
      chartId: 'ROAD-DARK',
      label: t('TOOLBAR_CHARTS-AND-MAPS_BASE-MAPS_ITEM_ROAD-DARK_LABEL'),
      image: mapRoadDark,
      isActive: mapLayerState.baseMap === 'ROAD-DARK'
    },
    {
      key: 'TERRAIN',
      chartId: 'TERRAIN',
      label: t('TOOLBAR_CHARTS-AND-MAPS_BASE-MAPS_ITEM_TERRAIN_LABEL'),
      image: mapTerrain,
      isActive: mapLayerState.baseMap === 'TERRAIN'
    },
    {
      key: 'SAT',
      chartId: 'SAT',
      label: t('TOOLBAR_CHARTS-AND-MAPS_BASE-MAPS_ITEM_SATELLITE_LABEL'),
      image: mapSat,
      isActive: mapLayerState.baseMap === 'SAT'
    },
    {
      key: 'CL-SAT',
      chartId: 'CL-SAT',
      label: t('TOOLBAR_CHARTS-AND-MAPS_BASE-MAPS_ITEM_CLEAN-SATELLITE_LABEL'),
      image: mapCleanSat,
      isActive: mapLayerState.baseMap === 'CL-SAT'
    }
  ]

  function handleOverlayChartClick(chartId: OverlayChartType) {
    const previousState = mapLayerState.overlayChart

    const newState: OverlayChartType = mapLayerState.overlayChart === chartId ? null : chartId

    mapLayerStateMutator.updateState({
      overlayChart: newState
    })

    if (previousState && previousState !== newState) {
      sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, {
        extra: previousState,
        active: false
      })
    }

    if (newState) {
      sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, {
        extra: newState,
        active: true
      })
    }
  }

  function handleOverlay2ChartClick(chartId: Overlay2ChartType) {
    const previousState = mapLayerState.overlay2Chart

    const newState: Overlay2ChartType = mapLayerState.overlay2Chart === chartId ? null : chartId

    mapLayerStateMutator.updateState({
      overlay2Chart: newState
    })

    if (previousState && previousState !== newState) {
      sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, {
        extra: previousState,
        active: false
      })
    }

    if (newState) {
      sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, {
        extra: newState,
        active: true
      })
    }
  }

  function handleOverlay3ChartClick(chartId: Overlay3ChartType) {
    const previousState = mapLayerState.overlay3Chart

    const newState: Overlay3ChartType = mapLayerState.overlay3Chart === chartId ? null : chartId

    mapLayerStateMutator.updateState({
      overlay3Chart: newState
    })

    if (previousState && previousState !== newState) {
      sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, {
        extra: previousState,
        active: false
      })
    }

    if (newState) {
      sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, {
        extra: newState,
        active: true
      })
    }
  }

  function handleBaseChartClick(chartId: BaseChartType) {
    const previousState = mapLayerState.baseChart

    const newState: BaseChartType = mapLayerState.baseChart === chartId ? null : chartId

    mapLayerStateMutator.updateState({
      baseChart: newState
    })

    if (previousState && previousState !== newState) {
      sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, {
        extra: previousState,
        active: false
      })
    }

    if (newState) {
      sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, {
        extra: newState,
        active: true
      })
    }
  }

  async function handleUserChartClick(chart: UserChart) {
    const id = chart.id.toString()
    const isActive = mapLayerState.userCharts.some((active) => active.id === id)

    if (isActive) {
      mapLayerStateMutator.removeActiveUserChart(id)
      sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, { extra: 'USER-CHART', active: false })
      sendMetrics(MetricsEvents.USER_CHARTS_TOGGLED, { chartId: id, action: 'disable', origin: 'toolbar' })
      return
    }

    const activated = await activateUserChart(chart)
    if (!activated) {
      ToastUtil.send({
        label: t('TOAST_USER-CHART_SHOW-ON-MAP_ERROR'),
        variant: 'error',
        dismissible: true
      })
      return
    }

    sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, { extra: 'USER-CHART', active: true })
    sendMetrics(MetricsEvents.USER_CHARTS_TOGGLED, { chartId: id, action: 'enable', origin: 'toolbar' })
  }

  function handleBaseMapClick(baseMapId: BaseMapType) {
    const previousState = { ...mapLayerState }

    const newState = mapLayerState.baseMap === baseMapId ? 'ROAD' : baseMapId

    mapLayerStateMutator.updateState({
      baseMap: newState,
      overlayChart: null,
      overlay2Chart: null,
      baseChart: null
    })

    if (previousState.overlayChart) {
      sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, {
        extra: previousState.overlayChart,
        active: false
      })
    }

    if (previousState.overlay2Chart) {
      sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, {
        extra: previousState.overlay2Chart,
        active: false
      })
    }

    if (previousState.baseChart) {
      sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, {
        extra: previousState.baseChart,
        active: false
      })
    }

    if (previousState.baseMap && previousState.baseMap !== newState) {
      sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, {
        extra: previousState.baseMap,
        active: false
      })
    }

    sendMetrics(MetricsEvents.TOOLBAR_CHARTS_MAP_SELECTED, {
      extra: newState,
      active: true
    })
  }

  return (
    <ChartsContainer>
      <SelectorsGrid collumns={4}>
        {overlayCharts.map((overlayChart) => (
          <ChartSelector
            key={overlayChart.key}
            id={overlayChart.key}
            size="sm"
            isActive={overlayChart.isActive}
            imageSrc={overlayChart.image}
            label={overlayChart.label}
            onChange={() => handleOverlayChartClick(overlayChart.chartId)}
          />
        ))}
        {overlay2Charts.map((overlay2Chart) => (
          <ChartSelector
            key={overlay2Chart.key}
            id={overlay2Chart.key}
            size="sm"
            isActive={overlay2Chart.isActive}
            imageSrc={overlay2Chart.image}
            label={overlay2Chart.label}
            onChange={() => handleOverlay2ChartClick(overlay2Chart.chartId)}
          />
        ))}
        {overlay3Charts.map((overlay3Chart) => (
          <ChartSelector
            key={overlay3Chart.key}
            id={overlay3Chart.key}
            size="sm"
            isActive={overlay3Chart.isActive}
            imageSrc={overlay3Chart.image}
            label={overlay3Chart.label}
            onChange={() => handleOverlay3ChartClick(overlay3Chart.chartId)}
          />
        ))}
      </SelectorsGrid>
      <SelectorsGrid collumns={3}>
        {baseCharts.map((baseChart) => (
          <ChartSelector
            key={baseChart.key}
            id={baseChart.key}
            size="md"
            isActive={baseChart.isActive}
            imageSrc={baseChart.image}
            label={baseChart.label}
            onChange={() => handleBaseChartClick(baseChart.chartId)}
          />
        ))}
      </SelectorsGrid>
      <SelectorsGrid collumns={3}>
        {baseMaps.map((baseMap) => (
          <ChartSelector
            key={baseMap.key}
            id={baseMap.key}
            size="md"
            isActive={baseMap.isActive}
            imageSrc={baseMap.image}
            label={baseMap.label}
            onChange={() => handleBaseMapClick(baseMap.chartId)}
          />
        ))}
      </SelectorsGrid>
      {userCharts.length > 0 && (
        <Accordion size="md">
          <AccordionItem title={t('TOOLBAR_CHARTS-AND-MAPS_USER-CHARTS_ACCORDION_TITLE')} size="md">
            {/* Stop clicks on the chart cards from bubbling to the AccordionItem
                container, which would otherwise toggle (collapse) the accordion. */}
            <div onClick={(e) => e.stopPropagation()}>
              <SelectorsGrid collumns={3}>
                {userCharts.map((chart) => (
                  <ChartSelector
                    key={chart.id.toString()}
                    id={`user-chart-${chart.id.toString()}`}
                    size="md"
                    clampLabel
                    isActive={mapLayerState.userCharts.some((active) => active.id === chart.id.toString())}
                    isLoading={activatingChartId === chart.id.toString()}
                    imageSrc={userChartPlaceholder}
                    label={chart.name}
                    onChange={() => handleUserChartClick(chart)}
                  />
                ))}
              </SelectorsGrid>
            </div>
          </AccordionItem>
        </Accordion>
      )}
    </ChartsContainer>
  )
}

export { ChartsTool }
